import React from 'react';

const WORKFLOW_STEPS = [
  {
    num: '01',
    title: 'Discover & Research',
    text: 'Understanding user needs, context, and goals before writing a single line of code — grounded in user-centered design principles from my UI course at uOttawa.',
  },
  {
    num: '02',
    title: 'Ideate & Prototype',
    text: 'Sketching ideas and creating wireframes to test concepts early. I apply heuristic evaluation and accessibility guidelines before investing in high-fidelity design.',
  },
  {
    num: '03',
    title: 'Build & Iterate',
    text: 'Implementing with clean, maintainable code and refining based on feedback. Every iteration brings the product closer to the user\'s real needs.',
  },
];

const EXPERIENCE_HIGHLIGHTS = [
  {
    company: 'Transport Canada',
    url: 'https://tc.canada.ca/en',
    role: 'Mobile App Developer — iOS & Android',
    period: 'May 2026 – Present',
    points: [
      'Developing mobile applications for iOS and Android on an Agile team',
      'Full-stack developer contributing to government digital services',
    ],
    color: '#a78bfa',
  },
  {
    company: 'Scotiabank',
    url: 'https://www.gbm.scotiabank.com/en.html',
    role: 'Technical Sales & Trading Intern (Incoming)',
    period: 'Sept 2026 – Dec 2026',
    points: [
      'Incoming intern on the Sales and Trading division (desk assignment TBD)',
    ],
    color: '#f5a623',
  },
  {
    company: 'Magnet Forensics',
    url: 'https://www.magnetforensics.com',
    role: 'Software Developer',
    period: 'Jan 2026 – Apr 2026',
    points: [
      'Full-stack Agile development with React, TypeScript, C# (.NET), Clojure, PostgreSQL',
      'Improved CI/CD build success rate by 95% across Jenkins pipelines & microservices',
      'Managed AWS S3 artifacts and ran integration tests on EC2 instances',
    ],
    color: '#1a6cf5',
  },
  {
    company: 'Telesat',
    url: 'https://www.telesat.com',
    role: 'Embedded Software Engineer',
    period: 'May 2025 – Aug 2025',
    points: [
      'Built 5+ automated Python smoke & regression tests in Pytest with Protobuf messages',
      'Designed a C++ carrier table library using coroutines, validating 100,000+ data points',
      'Built an MPLS packet sniffer for Docker/Kubernetes traffic with Wireshark',
    ],
    color: '#4d94ff',
  },
  {
    company: 'INRS — National Institute of Scientific Research',
    url: 'https://inrs.ca/en/',
    role: 'Software Developer',
    period: 'May 2024 – Aug 2024',
    points: [
      'Automated high-throughput experiment analysis with Python workflows',
      'Built data analytics & visualization tools using Matplotlib, Pandas, and NumPy',
    ],
    color: '#7eb8ff',
  },
];

const LINKS = [
  { label: 'SEG 3125 Analysis & Design of User Interface (uOttawa)', href: 'https://catalogue.uottawa.ca/en/courses/seg/#:~:text=SEG%C2%A03125%20Analysis%20and%20Design%20of%20User%20Interfaces%20(3%20units)' },
  { label: 'Bootstrap 5 Course (W3Schools)', href: 'https://www.w3schools.com/bootstrap5/index.php' },
  { label: 'JavaScript Course (W3Schools)', href: 'https://www.w3schools.com/js/default.asp' },
  { label: 'React Course (YouTube)', href: 'https://www.youtube.com/playlist?list=PL4cUxeGkcC9joIM91nLzd_qaH_AimmdAR' }
];

function WorkflowCard({ num, title, text }) {
  return (
    <div
      className="dark-card p-4 h-100"
      style={{ transition: 'border-color 0.2s, transform 0.2s', cursor: 'default' }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = 'rgba(77,148,255,0.45)';
        e.currentTarget.style.transform = 'translateY(-4px)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = 'var(--border)';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      <div
        style={{
          fontFamily: "'Syne', sans-serif",
          fontSize: '2.2rem', fontWeight: 800,
          background: 'linear-gradient(135deg, #4d94ff, #a78bfa)',
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          backgroundClip: 'text', lineHeight: 1, marginBottom: '0.6rem',
        }}
      >
        {num}
      </div>
      <h4 style={{ fontFamily: "'Syne', sans-serif", fontWeight: 600, fontSize: '1rem', color: 'var(--text)', marginBottom: '0.5rem' }}>
        {title}
      </h4>
      <p style={{ color: 'var(--muted)', fontSize: '0.88rem', lineHeight: 1.65, margin: 0 }}>{text}</p>
    </div>
  );
}

function ExperienceCard({ company, url, role, period, points, color }) {
  return (
    <div
      className="dark-card p-4"
      style={{ borderLeft: `3px solid ${color}`, transition: 'transform 0.2s' }}
      onMouseEnter={e => e.currentTarget.style.transform = 'translateX(4px)'}
      onMouseLeave={e => e.currentTarget.style.transform = 'translateX(0)'}
    >
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
        <div>
          <h5 style={{ fontFamily: "'Syne', sans-serif", fontWeight: 700, color: 'var(--text)', fontSize: '1rem', margin: 0 }}>
              <a href={url} target="_blank" rel="noreferrer" style={{ color: 'var(--text)', textDecoration: 'none' }}
                onMouseEnter={e => e.target.style.color = 'var(--blue-light)'}
                onMouseLeave={e => e.target.style.color = 'var(--text)'}
              >
                {company} ↗
              </a>
            </h5>
          <p style={{ color, fontSize: '0.85rem', margin: 0, fontWeight: 500 }}>{role}</p>
        </div>
        <span
          style={{
            background: 'rgba(26,108,245,0.1)',
            border: '1px solid rgba(26,108,245,0.2)',
            color: 'var(--blue-light)',
            borderRadius: 4, padding: '0.25rem 0.7rem',
            fontSize: '0.75rem', fontWeight: 500, whiteSpace: 'nowrap',
          }}
        >
          {period}
        </span>
      </div>
      <ul style={{ paddingLeft: '1.1rem', margin: 0 }}>
        {points.map((pt, i) => (
          <li key={i} style={{ color: 'var(--muted)', fontSize: '0.87rem', lineHeight: 1.65, marginBottom: '0.2rem' }}>
            {pt}
          </li>
        ))}
      </ul>
    </div>
  );
}

function HowIWork() {
  return (
    <section id="howIWork" className="section-wrapper">
      <p className="section-label">02. My Process</p>
      <h2 className="section-title">
        How I <span className="gradient-word">Work</span>
      </h2>

      {/* Workflow steps — Bootstrap row */}
      <div className="row g-4 mb-5">
        {WORKFLOW_STEPS.map(step => (
          <div className="col-md-4" key={step.num}>
            <WorkflowCard {...step} />
          </div>
        ))}
      </div>

      {/* Learning callout */}
      <div
        style={{
          background: 'rgba(26,108,245,0.07)',
          border: '1px solid rgba(26,108,245,0.2)',
          borderRadius: 10, padding: '1.8rem 2rem',
          marginBottom: '3.5rem',
        }}
      >
        <div className="d-flex gap-3 align-items-start">
          <span style={{ fontSize: '1.8rem', marginTop: 2, flexShrink: 0 }}>📚</span>
          <div>
            <p style={{ color: 'var(--text)', fontWeight: 600, marginBottom: '0.5rem', fontSize: '1rem' }}>
              Currently taking SEG 3125 — Analysis and Design of User Interfaces at uOttawa
            </p>
            <p style={{ color: 'var(--muted)', fontSize: '0.92rem', marginBottom: '0.9rem' }}>
              This course covers UI analysis and design principles including user-centered design, heuristic evaluation,
              accessibility, and prototyping. I reference these resources regularly alongside my coursework:</p>
            <div className="d-flex flex-wrap">
              {LINKS.map(({ label, href }) => (
                <a key={label} href={href} target="_blank" rel="noreferrer" className="ext-link">
                  ↗ {label}
                </a>
              ))}
            </div>
            <p style={{ color: 'var(--muted)', fontSize: '0.85rem', marginTop: '1rem', marginBottom: 0 }}>
              Throughout the semester I will add concepts such as{' '}
              <strong style={{ color: 'var(--text)' }}>heuristic evaluation</strong>,{' '}
              <strong style={{ color: 'var(--text)' }}>user-centered design</strong>,{' '}
              <strong style={{ color: 'var(--text)' }}>accessibility guidelines</strong>, and{' '}
              <strong style={{ color: 'var(--text)' }}>usability testing</strong> to document my growing design process.
            </p>
          </div>
        </div>
      </div>

      {/* Experience highlights */}
      <h3
        style={{
          fontFamily: "'Syne', sans-serif", fontSize: '1.35rem', fontWeight: 700,
          color: 'var(--text)', marginBottom: '1.5rem',
        }}
      >
        Professional Experience
      </h3>
      <div id="proExperience" className="d-flex flex-column gap-3">
        {EXPERIENCE_HIGHLIGHTS.map(exp => (
          <ExperienceCard key={exp.company} {...exp} />
        ))}
      </div>
    </section>
  );
}

export default HowIWork;
